/// Port of mobile/lib/auth/ui/auth_screen.dart.
/// Social / email chooser, then email → welcome back | register | social-only.
///
/// Deep links: `/auth?email=…&code=…` prefill the register OTP and auto-verify.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { appEnv } from '@/app/env/appEnv';
import { appRoutes } from '@/app/router/routes';
import { useSnackbarStore } from '@/app/ui/snackbar/snackbarStore';
import * as authRepository from '@/auth/data/authRepository';
import { prepareGoogleSignIn } from '@/auth/data/googleSignIn';
import {
  hasApple,
  hasGoogle,
  hasPasswordEmail,
  isNewEmail,
  type EmailStatus,
} from '@/auth/domain/emailStatus';
import { useAuthStore } from '@/auth/handler/authStore';
import { useThemeColors } from '@/settings/handler/settingsStore';
import { PageChrome } from '@/ui/PageChrome';
import { PixelActionButton, PixelLinkButton } from '@/ui/pixel/PixelActionButton';
import { PixelContainer } from '@/ui/pixel/PixelContainer';
import { PixelIcon } from '@/ui/pixel/PixelIcon';
import { PixelPasswordField } from '@/ui/pixel/PixelPasswordField';
import { validateUsername } from '@/user/domain/username';

type AuthStep = 'providers' | 'email' | 'newAccount' | 'otpCode' | 'setUsername' | 'welcomeBack' | 'socialOnly';

type AuthBusy =
  | null
  | 'lookup'
  | 'signIn'
  | 'signUp'
  | 'forgotPassword'
  | 'sendOtp'
  | 'verifyOtp'
  | 'resendOtp'
  | 'google';

export function AuthScreen() {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const deepEmail = (params.get('email') ?? '').trim();
  const deepCode = (params.get('code') ?? '').replace(/\D/g, '').slice(0, 4);
  const deepLinkHandled = useRef(false);

  const signIn = useAuthStore((s) => s.signIn);
  const signUp = useAuthStore((s) => s.signUp);
  const signInWithGoogle = useAuthStore((s) => s.signInWithGoogle);
  const changeUsername = useAuthStore((s) => s.changeUsername);
  const lookupEmail = useAuthStore((s) => s.lookupEmail);
  const clearError = useAuthStore((s) => s.clearError);
  const storeBusy = useAuthStore((s) => s.isSubmitting);
  const storeError = useAuthStore((s) => s.error);
  const signedInEmail = useAuthStore((s) => (s.user.isAnonymous ? '' : s.user.email));
  const showSuccess = useSnackbarStore((s) => s.success);
  const showInfo = useSnackbarStore((s) => s.info);

  const [step, setStep] = useState<AuthStep>('providers');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [proofToken, setProofToken] = useState<string | null>(null);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [emailStatus, setEmailStatus] = useState<EmailStatus | null>(null);
  const [busy, setBusy] = useState<AuthBusy>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  /** True when Google just created the account — finish via changeUsername. */
  const [socialUsernamePending, setSocialUsernamePending] = useState(false);
  const [registerOtpVerified, setRegisterOtpVerified] = useState(false);

  const error = localError ?? storeError;

  const leaveAuth = (withSuccess = false): void => {
    if (withSuccess) showSuccess(t('authSignInSuccess'));
    if (window.history.length > 1) navigate(-1);
    else navigate(appRoutes.home);
  };
  const googleReady = appEnv.googleClientId.length > 0;
  const waiting = storeBusy || busy !== null;

  const runBusy = async (action: NonNullable<AuthBusy>, body: () => Promise<void>): Promise<void> => {
    if (busy !== null) return;
    setBusy(action);
    setLocalError(null);
    clearError();
    try {
      await body();
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    if (!googleReady) return;
    void prepareGoogleSignIn(appEnv.googleClientId);
  }, [googleReady]);

  useEffect(() => {
    if (resendSeconds <= 0) return;
    const timer = window.setTimeout(() => setResendSeconds((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendSeconds]);

  useEffect(() => {
    if (deepLinkHandled.current) return;
    if (!deepEmail || deepCode.length !== 4) return;
    deepLinkHandled.current = true;
    setEmail(deepEmail);
    setOtpCode(deepCode);
    setStep('otpCode');
    void applyRegisterDeepLink(deepCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot deep link
  }, [deepEmail, deepCode]);

  const goProviders = (): void => {
    setStep('providers');
    setEmailStatus(null);
    setLocalError(null);
    clearError();
    setPassword('');
    setConfirmPassword('');
    setUsername('');
    setOtpCode('');
    setProofToken(null);
    setRegisterOtpVerified(false);
    setResendSeconds(0);
    setSocialUsernamePending(false);
  };

  const goEmail = (): void => {
    setStep('email');
    setEmailStatus(null);
    setLocalError(null);
    clearError();
    setPassword('');
    setConfirmPassword('');
    setUsername('');
    setOtpCode('');
    setProofToken(null);
    setRegisterOtpVerified(false);
    setResendSeconds(0);
    setSocialUsernamePending(false);
  };

  const onBack = (): void => {
    switch (step) {
      case 'providers':
        leaveAuth();
        return;
      case 'email':
        goProviders();
        return;
      case 'welcomeBack':
      case 'socialOnly':
        goEmail();
        return;
      case 'setUsername':
        setStep('otpCode');
        return;
      case 'otpCode':
        setStep('newAccount');
        return;
      case 'newAccount':
        goEmail();
        return;
    }
  };

  const mustSetUsername = step === 'setUsername';

  const title = (): string => {
    switch (step) {
      case 'welcomeBack':
      case 'socialOnly':
        return t('authWelcomeBackTitle');
      case 'newAccount':
        return t('authSignUpTitle');
      case 'setUsername':
        return t('authSetUsernameTitle');
      case 'otpCode':
        return t('authOtpTitle');
      default:
        return t('authSignInTitle');
    }
  };

  const continueWithEmail = async (): Promise<void> => {
    const trimmed = email.trim();
    if (!trimmed) {
      setLocalError(t('authEmailRequired'));
      return;
    }
    await runBusy('lookup', async () => {
      const status = await lookupEmail(trimmed);
      setEmailStatus(status);
      if (isNewEmail(status)) {
        setPassword('');
        setConfirmPassword('');
        setUsername('');
        setOtpCode('');
        setProofToken(null);
        setStep('newAccount');
      } else if (hasPasswordEmail(status)) setStep('welcomeBack');
      else setStep('socialOnly');
    });
  };

  const submitNewAccountPassword = async (): Promise<void> => {
    if (password.length < 8) {
      setLocalError(t('profilePasswordTooShort'));
      return;
    }
    if (password !== confirmPassword) {
      setLocalError(t('authPasswordMismatch'));
      return;
    }
    if (registerOtpVerified && proofToken) {
      setStep('setUsername');
      return;
    }
    await runBusy('sendOtp', async () => {
      await authRepository.sendOtp(email.trim(), 'register');
      setOtpCode('');
      setResendSeconds(60);
      setStep('otpCode');
    });
  };

  const submitLogin = async (): Promise<void> => {
    await runBusy('signIn', async () => {
      if (await signIn(email.trim(), password)) leaveAuth(true);
    });
  };

  const submitForgotPassword = async (): Promise<void> => {
    const trimmed = email.trim();
    if (!trimmed) {
      setLocalError(t('authEmailRequired'));
      return;
    }
    await runBusy('forgotPassword', async () => {
      await authRepository.forgotPassword(trimmed);
      setLocalError(t('authForgotPasswordSent'));
    });
  };

  const submitRegister = async (): Promise<void> => {
    if (!proofToken) {
      setLocalError(t('authOtpExpired'));
      return;
    }
    const invalid = validateUsername(username);
    if (invalid === 'length') {
      setLocalError(t('profileUsernameLength'));
      return;
    }
    if (invalid === 'chars') {
      setLocalError(t('profileUsernameChars'));
      return;
    }
    await runBusy('signUp', async () => {
      if (await signUp(email.trim(), password, proofToken, username.trim())) leaveAuth(true);
    });
  };

  const submitSocialUsername = async (): Promise<void> => {
    const invalid = validateUsername(username);
    if (invalid === 'length') {
      setLocalError(t('profileUsernameLength'));
      return;
    }
    if (invalid === 'chars') {
      setLocalError(t('profileUsernameChars'));
      return;
    }
    await runBusy('signUp', async () => {
      await changeUsername(username.trim());
      setSocialUsernamePending(false);
      leaveAuth(true);
    });
  };

  const submitUsername = async (): Promise<void> => {
    if (socialUsernamePending) {
      await submitSocialUsername();
    } else {
      await submitRegister();
    }
  };

  const verifyOtp = async (codeOverride?: string): Promise<void> => {
    const trimmed = email.trim();
    const code = (codeOverride ?? otpCode).trim();
    if (code.length !== 4) {
      setLocalError(t('authOtpInvalid'));
      return;
    }
    await runBusy('verifyOtp', async () => {
      const proof = await authRepository.verifyOtp(trimmed, code, 'register');
      setProofToken(proof);
      setSocialUsernamePending(false);
      setRegisterOtpVerified(true);
      if (password.length === 0) {
        setStep('newAccount');
      } else {
        setStep('setUsername');
      }
    });
  };

  const applyRegisterDeepLink = async (code: string): Promise<void> => {
    if (!deepEmail || code.length !== 4) return;
    await verifyOtp(code);
  };

  const onOtpChange = (value: string): void => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    setOtpCode(digits);
    if (digits.length === 4 && busy === null) {
      void verifyOtp(digits);
    }
  };

  const resendOtp = async (): Promise<void> => {
    if (resendSeconds > 0) return;
    await runBusy('resendOtp', async () => {
      await authRepository.sendOtp(email.trim(), 'register');
      setResendSeconds(60);
    });
  };

  const submitGoogle = async (): Promise<void> => {
    await runBusy('google', async () => {
      const outcome = await signInWithGoogle();
      if (outcome === false) {
        if (!useAuthStore.getState().error) {
          showInfo(t('authSocialCanceled', { provider: t('authProviderGoogle') }));
        }
        return;
      }
      if (outcome === 'needsUsername') {
        setUsername('');
        setLocalError(null);
        setSocialUsernamePending(true);
        setStep('setUsername');
        return;
      }
      if (outcome === 'done') leaveAuth(true);
    });
  };

  const showGoogle = googleReady && (emailStatus === null || hasGoogle(emailStatus));
  const appleOnly = emailStatus !== null && hasApple(emailStatus) && !hasGoogle(emailStatus);
  const setUsernameEmailLine = socialUsernamePending ? signedInEmail : email.trim();
  const showSetUsernameEmail =
    setUsernameEmailLine.length > 0 && !setUsernameEmailLine.endsWith('.placeholder');

  return (
    <PageChrome title={title()} onBack={onBack} showBack={!mustSetUsername}>
      <div className="flex-1 overflow-y-auto px-6 pb-10 pt-5">
        {step === 'providers' && (
          <>
            <p className="mb-7 leading-tight" style={{ fontSize: 16, color: colors.onSurface }}>
              {t('authChooseMethodHint')}
            </p>
            {googleReady && (
              <PixelActionButton
                label={t('authContinueWithGoogle')}
                variant="tonal"
                fontSize={18}
                leading={<PixelIcon name="google" size={22} color={colors.onSurface} />}
                busy={busy === 'google'}
                onClick={waiting ? undefined : () => void submitGoogle()}
              />
            )}
            <PixelActionButton
              label={t('authContinueWithEmail')}
              variant="tonal"
              fontSize={18}
              onClick={waiting ? undefined : goEmail}
              className={googleReady ? 'mt-3' : ''}
            />
          </>
        )}

        {step === 'email' && (
          <>
            <p className="mb-5 leading-tight" style={{ fontSize: 16, color: colors.onSurface }}>
              {t('authEmailStepHint')}
            </p>
            <Field
              label={t('authEmail')}
              value={email}
              onChange={setEmail}
              type="email"
              autoComplete="email"
              disabled={waiting}
            />
            <PixelActionButton
              label={t('authContinue')}
              busy={busy === 'lookup'}
              className="mt-2"
              onClick={waiting ? undefined : () => void continueWithEmail()}
            />
          </>
        )}

        {step === 'newAccount' && (
          <>
            <p className="leading-tight" style={{ fontSize: 16, color: colors.onSurface }}>
              {t('authCreateAccountHint')}
            </p>
            <p className="mb-5 mt-2 leading-tight" style={{ fontSize: 16, color: colors.onSurface, opacity: 0.6 }}>
              {email.trim()}
            </p>
            <PasswordField
              label={t('authPassword')}
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              disabled={waiting}
            />
            <PasswordField
              label={t('authConfirmPassword')}
              value={confirmPassword}
              onChange={setConfirmPassword}
              autoComplete="new-password"
              disabled={waiting}
            />
            <PixelActionButton
              label={t('authContinue')}
              busy={busy === 'sendOtp'}
              className="mt-2"
              onClick={waiting ? undefined : () => void submitNewAccountPassword()}
            />
          </>
        )}

        {step === 'otpCode' && (
          <>
            <p className="mb-5 leading-tight" style={{ fontSize: 16, color: colors.onSurface }}>
              {t('authOtpHint', { email: email.trim() })}
            </p>
            <Field
              label={t('authOtpCode')}
              value={otpCode}
              onChange={onOtpChange}
              type="text"
              name="one-time-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              disabled={waiting}
              maxLength={4}
              pattern="[0-9]*"
            />
            <PixelActionButton
              label={t('authContinue')}
              busy={busy === 'verifyOtp'}
              className="mt-2"
              onClick={waiting ? undefined : () => void verifyOtp()}
            />
            <PixelLinkButton
              label={resendSeconds > 0 ? t('authOtpResendWait', { seconds: resendSeconds }) : t('authOtpResend')}
              busy={busy === 'resendOtp'}
              disabled={waiting || resendSeconds > 0}
              onClick={() => void resendOtp()}
            />
          </>
        )}

        {step === 'welcomeBack' && (
          <>
            <p className="leading-tight" style={{ fontSize: 16, color: colors.onSurface }}>
              {emailStatus?.username
                ? t('authWelcomeBackNamed', { username: emailStatus.username })
                : t('authWelcomeBackHint')}
            </p>
            <p className="mb-5 mt-2 leading-tight" style={{ fontSize: 16, color: colors.onSurface, opacity: 0.6 }}>
              {email.trim()}
            </p>
            <PasswordField
              label={t('authPassword')}
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              disabled={waiting}
            />
            <PixelActionButton
              label={t('authSignIn')}
              busy={busy === 'signIn'}
              className="mt-2"
              onClick={waiting ? undefined : () => void submitLogin()}
            />
            <PixelLinkButton
              label={t('authForgotPassword')}
              busy={busy === 'forgotPassword'}
              disabled={waiting}
              onClick={() => void submitForgotPassword()}
            />
            {showGoogle && (
              <>
                <OrDivider />
                <PixelActionButton
                  label={t('authContinueWithGoogle')}
                  variant="tonal"
                  fontSize={18}
                  leading={<PixelIcon name="google" size={22} color={colors.onSurface} />}
                  busy={busy === 'google'}
                  onClick={waiting ? undefined : () => void submitGoogle()}
                />
              </>
            )}
          </>
        )}

        {step === 'setUsername' && (
          <>
            <p className="leading-tight" style={{ fontSize: 16, color: colors.onSurface }}>
              {t('authSetUsernameHint')}
            </p>
            {showSetUsernameEmail ? (
              <p className="mb-5 mt-2 leading-tight" style={{ fontSize: 16, color: colors.onSurface, opacity: 0.6 }}>
                {setUsernameEmailLine}
              </p>
            ) : (
              <div className="mb-5" />
            )}
            <Field
              label={t('authUsername')}
              value={username}
              onChange={setUsername}
              autoComplete="username"
              disabled={waiting}
            />
            <p className="mb-2 leading-tight" style={{ fontSize: 14, color: colors.onSurface, opacity: 0.6 }}>
              {t('profileUsernameHint')}
            </p>
            <PixelActionButton
              label={t('authSignUp')}
              busy={busy === 'signUp'}
              className="mt-2"
              onClick={waiting ? undefined : () => void submitUsername()}
            />
          </>
        )}

        {step === 'socialOnly' && (
          <>
            <p className="leading-tight" style={{ fontSize: 16, color: colors.onSurface }}>
              {emailStatus?.username
                ? t('authSocialOnlyNamed', { username: emailStatus.username })
                : t('authSocialOnlyHint')}
            </p>
            <p className="mb-6 mt-2 leading-tight" style={{ fontSize: 16, color: colors.onSurface, opacity: 0.6 }}>
              {email.trim()}
            </p>
            {showGoogle && (
              <PixelActionButton
                label={t('authContinueWithGoogle')}
                variant="tonal"
                fontSize={18}
                leading={<PixelIcon name="google" size={22} color={colors.onSurface} />}
                busy={busy === 'google'}
                onClick={waiting ? undefined : () => void submitGoogle()}
              />
            )}
            {appleOnly && (
              <p className="leading-tight" style={{ fontSize: 16, color: colors.onSurface }}>
                {t('authSocialAppleOnlyAndroid')}
              </p>
            )}
          </>
        )}

        {error && (
          <p className="mt-3 leading-tight" style={{ fontSize: 16, color: '#ef5350' }}>
            {error}
          </p>
        )}
      </div>
    </PageChrome>
  );
}

function OrDivider() {
  const colors = useThemeColors();
  const { t } = useTranslation();
  return (
    <div className="my-7 flex items-center gap-3">
      <div className="h-px flex-1" style={{ backgroundColor: colors.onSurface, opacity: 0.3 }} />
      <span className="leading-none" style={{ fontSize: 16, color: colors.onSurface }}>
        {t('authOr')}
      </span>
      <div className="h-px flex-1" style={{ backgroundColor: colors.onSurface, opacity: 0.3 }} />
    </div>
  );
}

function PasswordField({
  label,
  value,
  onChange,
  autoComplete,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  disabled?: boolean;
}) {
  const colors = useThemeColors();
  return (
    <label className="mb-4 block">
      <span className="mb-2 block leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
        {label}
      </span>
      <PixelPasswordField
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        disabled={disabled}
      />
    </label>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  name,
  autoComplete,
  inputMode,
  maxLength,
  pattern,
  autoFocus,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  name?: string;
  autoComplete?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  maxLength?: number;
  pattern?: string;
  autoFocus?: boolean;
  disabled?: boolean;
}) {
  const colors = useThemeColors();
  return (
    <label className="mb-4 block">
      <span className="mb-2 block leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
        {label}
      </span>
      <PixelContainer
        radius={3}
        pixelScale={2}
        padding="4px 12px"
        backgroundColor={colors.surfaceContainerHighest}
        borderColor={colors.onSurface}
      >
        <input
          type={type}
          name={name}
          value={value}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          inputMode={inputMode}
          maxLength={maxLength}
          pattern={pattern}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          className="w-full bg-transparent outline-none"
          style={{
            fontSize: 20,
            color: colors.onSurface,
            lineHeight: 1.2,
            fontFamily: 'inherit',
            border: 'none',
            padding: '6px 0',
          }}
        />
      </PixelContainer>
    </label>
  );
}
